import {
  CreditCard,
  Calendar,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  User,
  ChevronRight,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { PaymentBatch } from '../../types';
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
} from '../../types';

function StatusIcon({ status }: { readonly status: PaymentBatch['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    case 'processing':
      return <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />;
    case 'approved':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'pending_approval':
      return <Clock className="h-4 w-4 text-amber-500" />;
    default:
      return <FileText className="h-4 w-4 text-gray-500" />;
  }
}

interface PaymentBatchCardProps {
  readonly batch: PaymentBatch;
  readonly onApprove?: (batchId: string) => void;
  readonly onProcess?: (batchId: string) => void;
  readonly isApproving?: boolean;
  readonly isProcessing?: boolean;
}

export function PaymentBatchCard({
  batch,
  onApprove,
  onProcess,
  isApproving,
  isProcessing,
}: PaymentBatchCardProps) {
  const statusColor = PAYMENT_STATUS_COLORS[batch.status];
  const canApprove = batch.status === 'pending_approval';
  const canProcess = batch.status === 'approved';

  return (
    <Card className="hover:shadow-md transition-shadow group">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{batch.batchNumber}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
              {PAYMENT_STATUS_LABELS[batch.status]}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <StatusIcon status={batch.status} />
          <span>{PAYMENT_METHOD_LABELS[batch.paymentMethod]}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileText className="h-4 w-4" />
          <span>{batch.invoiceCount} invoice{batch.invoiceCount === 1 ? '' : 's'}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>Scheduled: {new Date(batch.scheduledDate).toLocaleDateString()}</span>
        </div>

        {batch.processedDate && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span>Processed: {new Date(batch.processedDate).toLocaleDateString()}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-4 w-4" />
          <span>Created by: {batch.createdBy}</span>
        </div>

        {batch.approvedBy && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="h-4 w-4" />
            <span>Approved by: {batch.approvedBy}</span>
          </div>
        )}

        {batch.notes && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-muted pl-2">
            {batch.notes}
          </p>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Suppliers:</p>
          <div className="space-y-1">
            {batch.items.slice(0, 3).map((item) => (
              <div key={item.invoiceId} className="flex items-center justify-between text-sm">
                <span className="truncate flex-1">{item.supplierName}</span>
                <span className="text-muted-foreground ml-2">
                  ${item.amount.toLocaleString()}
                </span>
              </div>
            ))}
            {batch.items.length > 3 && (
              <p className="text-xs text-muted-foreground">
                +{batch.items.length - 3} more suppliers
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-1 text-primary">
            <DollarSign className="h-4 w-4" />
            <span className="text-lg font-bold">{batch.totalAmount.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground">{batch.currency}</span>
          </div>
        </div>

        {(canApprove || canProcess) && (
          <div className="flex gap-2 pt-2">
            {canApprove && onApprove && (
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(batch.batchId);
                }}
                disabled={isApproving}
              >
                {isApproving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Approve
              </Button>
            )}
            {canProcess && onProcess && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onProcess(batch.batchId);
                }}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Process
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
